import Vue from 'vue'
import axios from 'axios'
import Qs from 'qs'
import VueAxios from 'vue-axios'
import md5 from 'md5'
import moment from 'moment'
import router from '../router'
import { get, includes } from 'lodash'
import axiosCancel from 'axios-cancel'
import store from '../store'
import session from './session'
import { sendAnalyticsEvent } from './googleAnalytics'
import { notification } from './helpers'
import { AlertTypes } from './alerts_types'
import { mensagemUsuarioBloqueado } from '../enums/usuario_bloqueado/mensagemUsuarioBloqueado'

const http = axios.create({
  baseURL: process.env.VUE_APP_WS_ROOT,
  timeout: 600000
})

export default http

axiosCancel(http)

Vue.use(VueAxios, http)

let requestId = 0
const VERSAO_FRONT_ATUAL = window.appVersion || process.env.VUE_APP_VERSION || null

http.interceptors.request.use(function (config) {
  config['requestId'] = get(config, 'requestId', ++requestId)

  // envia a chave dinamica do ws
  config.headers['X-Key'] = md5('newm' + moment().utc().format('YY-MM-DD-HH'))

  if (session.exists()) {
    // envia o token JWT
    config.headers['Authorization'] = `Bearer ${session.get('token')}`
  }

  if (VERSAO_FRONT_ATUAL) {
    config.headers['X-Client-Version'] = VERSAO_FRONT_ATUAL
  }

  const brokerPoliticaVendas = session.get('broker_politica_vendas')
  const brokerFluxoVendasComum = session.get('broker_fluxo_vendas_comum')

  config.headers['X-Broker-Politica-Vendas'] = (brokerPoliticaVendas === true) ? 'true' : 'false'
  config.headers['X-Broker-Fluxo-Vendas-Comum'] = (brokerFluxoVendasComum === true) ? 'true' : 'false'

  const options = config['options'] = {
    silent: false,
    showAdditionalLoadingText: false,
    unguarded: false,
    loading: null,
    ...config['options']
  }

  /* if (!options.unguarded && router.currentRoute.meta.public) {
    options.unguarded = true
  } */

  if (options.showAdditionalLoadingText) {
    store.commit('setShowAdditionalLoadingText', true)
  } else {
    store.commit('setShowAdditionalLoadingText', false)
  }

  options.unguarded = true
  store.commit('resetCarregarLoading')
  if (options.loading) {
    store.commit('setCarregarLoading', options.loading)
  }
  if (!options.silent) {
    store.commit('loadingStart')
  }

  if (!config.url) {
    config.url = '/'
  }

  config.paramsSerializer = params => {
    return Qs.stringify(params, {
      arrayFormat: 'brackets',
      encode: true
    })
  }

  if (!options.unguarded && session.exists() && session.get('autenticar')) {
    if (process.env.NODE_ENV === 'development') {
      console.info(`» ${config.url}\tAguardando autenticação...`)
    }
    throw new AutenticarError(config, session.get('senha_invalida'))
  }

  return config
}, error => {
  const silent = get(error, 'config.options.silent', false)
  if (!silent) {
    store.commit('loadingDone')
  }
  return Promise.reject(error)
})

const RELOAD_FLAG_KEY = 'version_reload_done'

http.interceptors.response.use(function (response) {
  const silent = get(response, 'config.options.silent', false)
  const disableShowAdditionalLoadingText = get(response, 'config.options.showAdditionalLoadingText', false)
  const barraLoading = get(response, 'config.options.loading', false)

  if (disableShowAdditionalLoadingText) {
    store.commit('setShowAdditionalLoadingText', false)
  }

  if (!silent) {
    store.commit('loadingDone')
  }

  const token = response.headers['authorization']
  if (token) {
    session.set('token', token.replace(/^(Bearer )/, ''))
  }

  if (barraLoading) {
    return new Promise(resolve => {
      setTimeout(() => {
        resolve(response)
      }, 750)
    })
  } else {
    return response
  }
}, error => {
  sendAnalyticsEvent(Vue.$gtag, 'erro_navegacao', { 'error': error })

  const silent = get(error, 'config.options.silent', false)
  if (!silent) {
    store.commit('loadingDone')
  }

  const status = get(error, 'response.status', 500)
  const unguarded = get(error, 'config.options.unguarded', false)

  const mensagem = get(error, 'response.data.Msg_Erro', '')
  if (mensagem === mensagemUsuarioBloqueado.USER_BLOCKED) {
    const permiteLogar = session.get('permite_logar_tela_login')
    session.destroy()
    notification(AlertTypes.ERROR, mensagem)
    if (!permiteLogar) {
      router.replace('/acesso-bloqueado')
    } else {
      router.replace('/login')
    }
    return Promise.reject(error)
  }

  if (status === 426 && mensagem) {
    const jaFezReload = sessionStorage.getItem(RELOAD_FLAG_KEY)

    if (!jaFezReload) {
      sessionStorage.setItem(RELOAD_FLAG_KEY, 'true')
      window.location.reload()
    } else {
      sessionStorage.removeItem(RELOAD_FLAG_KEY)

      const sessaoPainel = session.get('sessao_painel') === true || session.get('sessao_painel') === 'true'
      const permiteLogar = session.get('permite_logar_tela_login')
      const base = window.location.origin + window.location.pathname

      let destino
      if (sessaoPainel) {
        destino = '#/painel/login'
      } else {
        destino = permiteLogar ? '#/login' : '#/logout-success'
      }

      localStorage.setItem('mensagem_redirect', mensagem)

      session.destroy()
      window.location.href = base + destino
    }

    return Promise.reject(error)
  }

  if (error instanceof AutenticarError || (!unguarded && session.exists() && includes([401, 403], status))) {
    const senha_invalida = get(error, 'response.data.senha_invalida', error.senha_invalida)

    const config = error.config

    if (config.url.startsWith(config.baseURL)) {
      config.url = config.url.substr(config.baseURL.length)
    }

    return new Promise((resolve, reject) => {
      store.commit('setAutenticar', { senha_invalida, resolve, reject: () => reject(error) })
    }).then(() => http.request(config))
  } else if (router.currentRoute.name === 'cliente-pagamento' && includes([401, 403], status)) {
    try {
      router.replace({ name: 'cliente-login' })
    } catch (e) {
      // do nothing
    }
  } else {
    return Promise.reject(error)
  }
})

router.beforeEach((to, from, next) => {
  http.cancelAll()
  next()
})

router.afterEach(() => {
  const mensagem = localStorage.getItem('mensagem_redirect')
  if (mensagem) {
    notification(AlertTypes.WARN, mensagem, '', false, false, true)
    localStorage.removeItem('mensagem_redirect')
  }
})

class AutenticarError extends Error {
  constructor (config, senha_invalida) {
    super()
    this.config = config
    this.senha_invalida = !!senha_invalida
  }
}
